import { useEffect, useState } from 'react';

/**
 * Generic hook for fetching a list from an async function.
 * @param {Function} fetchFn - The async function to fetch data (should return a promise).
 * @param {Array} deps - Dependency array for useEffect.
 * @param {any[]} fetchArgs - Arguments to pass to fetchFn.
 * @param {Function} [sortFn] - Optional sort function for the result.
 * @returns {[Array, boolean, string]} [data, loading, error]
 */
export default function useFetchList(fetchFn, deps = [], fetchArgs = [], sortFn) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const shouldFetch = fetchArgs.length === 0 || fetchArgs.every(Boolean);
    let isMounted = true;
    if (!shouldFetch) {
      setData([]);
      setLoading(false);
      setError('');
      return () => {};
    }
    setLoading(true);
    setError('');
    fetchFn(...fetchArgs)
      .then((res) => {
        if (!isMounted) return;
        let result = res.data ?? [];
        if (sortFn) result = result.sort(sortFn);
        setData(result);
        setLoading(false);
      })
      .catch(() => {
        if (!isMounted) return;
        setData([]);
        setLoading(false);
        setError('Failed to fetch data.');
      });
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return [data, loading, error];
}
